/** Ruleset editor (/notifications/rulesets/:id) — name/codename/match-type +
 *  the shared RulesEditor, with a Used-in tab and a guarded delete.
 *
 * Saves PATCH the sparse meta keys plus the COMPLETE rules list when dirty
 * (reconcile-by-id), carry an `expected_updated_ts` optimistic-concurrency
 * token (409 → ConflictBanner), and distribute 400s onto the fields / rule
 * rows. Deleting is refused (409, `ruleset_in_use`) while notifications,
 * rulesets, strings or campaigns reference the ruleset.
 */
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ConsoleApiError } from '@/api/client';
import * as rulesetsApi from '@/api/notificationRulesets';
import type { RulesetUsedByCounts, RulesetWrite } from '@/api/notificationRulesets';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ConflictBanner, EditorSaveBar } from '@/components/EditorSaveBar/EditorSaveBar';
import { FieldHelp } from '@/components/FieldHelp';
import { HistoryPanel } from '@/components/History/HistoryPanel';
import { PageHeader } from '@/components/PageHeader';
import { operatorActions } from '@/components/ValueActions/identity';
import { act, DetailSubtitle, ValueActions } from '@/components/ValueActions/ValueActions';
import { useSnackbar } from '@/components/Snackbar';
import { UsedByPanel } from '@/components/UsedByPanel/UsedByPanel';
import { usePermissions } from '@/hooks/usePermissions';

import { RULESET_HELP } from './fieldHelp';
import { codenameSearchActions } from './notificationLinks';
import { FieldActionsAdornment } from '@/components/ValueActions/FieldActionsAdornment';
import { RulesEditor } from './rules/RulesEditor';
import { useRuleIssues } from './rules/useRuleIssues';
import { useRulesetDraft } from './useRulesetDraft';

const MANAGE_PERM = 'console_notification_rulesets_manage';
const CODENAME_RE = /^[A-Za-z][A-Za-z0-9_-]*$/;

function usedByTotal(counts: RulesetUsedByCounts | undefined): number {
  if (!counts) return 0;
  return (
    counts.rules_in_notifications +
    counts.rules_in_rulesets +
    counts.notification_strings +
    counts.campaigns
  );
}

export function RulesetEditPage() {
  const { id: idStr } = useParams<{ id: string }>();
  const id = Number(idStr);
  const validId = Number.isFinite(id);
  const navigate = useNavigate();
  useEffect(() => {
    if (!validId) navigate('/notifications/rulesets', { replace: true });
  }, [validId, navigate]);
  if (!validId) return null;
  return <RulesetEditor key={id} id={id} />;
}

function RulesetEditor({ id }: { id: number }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const snackbar = useSnackbar();
  const { hasPerm } = usePermissions();
  const canManage = hasPerm(MANAGE_PERM);

  const detailQuery = useQuery({
    queryKey: ['notification_ruleset', id],
    queryFn: () => rulesetsApi.retrieve(id),
  });
  const detail = detailQuery.data;
  const draft = useRulesetDraft(detail);
  // Client-side rule-args validation (same gating as the server: new/changed rows block).
  const ruleIssues = useRuleIssues(draft.draft.rules, detail?.rules);

  const [tab, setTab] = useState<'general' | 'used_by' | 'history'>('general');
  const [conflict, setConflict] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [ruleErrors, setRuleErrors] = useState<Record<string, string>>({});
  const [deleted, setDeleted] = useState(false);
  useEffect(() => {
    if (deleted) navigate('/notifications/rulesets');
  }, [deleted, navigate]);

  const clearErrors = useCallback(() => {
    setFieldErrors({});
    setRuleErrors({});
  }, []);

  const applyServerErrors = useCallback((body: unknown) => {
    if (!body || typeof body !== 'object') return false;
    const fields: Record<string, string> = {};
    const rules: Record<string, string> = {};
    for (const [key, msg] of Object.entries(body as Record<string, unknown>)) {
      if (key === 'rules' && msg && typeof msg === 'object' && !Array.isArray(msg)) {
        for (const [idx, rowErrors] of Object.entries(msg as Record<string, unknown>)) {
          // Row messages are self-contained ("Comparison is required — …"), so
          // the field key (args / tpl / nested_ruleset …) is not repeated.
          rules[idx] =
            rowErrors && typeof rowErrors === 'object'
              ? Object.values(rowErrors as Record<string, unknown>)
                  .map((m) => (Array.isArray(m) ? m.join(' ') : String(m)))
                  .join(' ')
              : String(rowErrors);
        }
      } else {
        fields[key] = Array.isArray(msg) ? msg.join(' ') : String(msg);
      }
    }
    setFieldErrors(fields);
    setRuleErrors(rules);
    return Object.keys(fields).length > 0 || Object.keys(rules).length > 0;
  }, []);

  const saveMutation = useMutation({
    mutationFn: () => {
      const extra: Pick<RulesetWrite, 'expected_updated_ts'> = {};
      if (detail?.updated_ts) extra.expected_updated_ts = detail.updated_ts;
      return rulesetsApi.update(id, draft.buildPatch(extra));
    },
    onSuccess: (result) => {
      clearErrors();
      setConflict(false);
      draft.reset(result);
      qc.setQueryData(['notification_ruleset', id], result);
      void qc.invalidateQueries({ queryKey: ['notification_rulesets'] });
      snackbar.show('Ruleset saved.', 'success');
    },
    onError: (e) => {
      if (e instanceof ConsoleApiError && e.status === 400) {
        const mapped = applyServerErrors(e.body);
        snackbar.show(
          mapped ? 'The save was rejected — check the highlighted fields.' : e.message,
          'error',
        );
        return;
      }
      if (e instanceof ConsoleApiError && e.status === 409) {
        setConflict(true);
        return;
      }
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Save failed.', 'error');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => rulesetsApi.remove(id),
    onSuccess: () => {
      snackbar.show('Ruleset deleted.', 'success');
      void qc.invalidateQueries({ queryKey: ['notification_rulesets'] });
      setDeleted(true);
    },
    onError: (e) => {
      if (e instanceof ConsoleApiError && e.status === 409) {
        const body = (e.body ?? {}) as { used_by?: RulesetUsedByCounts };
        const c = body.used_by;
        const parts = c
          ? [
              c.rules_in_notifications && `${c.rules_in_notifications} notification rule(s)`,
              c.rules_in_rulesets && `${c.rules_in_rulesets} ruleset rule(s)`,
              c.notification_strings && `${c.notification_strings} notification string(s)`,
              c.campaigns && `${c.campaigns} campaign(s)`,
            ].filter(Boolean)
          : [];
        snackbar.show(
          `Cannot delete: still referenced by ${parts.join(', ') || 'other records'} — see the Used-in tab.`,
          'error',
        );
        setTab('used_by');
        return;
      }
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Delete failed.', 'error');
    },
  });

  const handleConflictReload = useCallback(() => {
    void detailQuery.refetch().then((res) => {
      if (res.data) {
        draft.reset(res.data);
        clearErrors();
        setConflict(false);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailQuery.refetch, draft.reset, clearErrors]);

  if (detailQuery.isLoading || !detail) {
    if (detailQuery.isError) {
      return (
        <Alert severity="error" sx={{ mt: 2 }}>
          Couldn’t load ruleset #{id}.
        </Alert>
      );
    }
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const codenameInvalid =
    draft.draft.codename.trim() !== '' && !CODENAME_RE.test(draft.draft.codename.trim());
  const title = draft.draft.name || `Ruleset #${detail.id}`;
  const tenantLabel = detail.tenant
    ? `${detail.tenant.code} — ${detail.tenant.name}`
    : 'System (usable by every operator)';
  const referenced = usedByTotal(detail.used_by);

  return (
    <Box>
      <PageHeader
        helpStory="notification-rulesets"
        title={title}
        subtitle={
          <DetailSubtitle
            parts={[
              detail.tenant ? (
                <ValueActions actions={operatorActions(detail.tenant)}>{tenantLabel}</ValueActions>
              ) : (
                tenantLabel
              ),
              <ValueActions key="id" actions={[act.copy(String(detail.id), 'Copy id')]}>ID {detail.id}</ValueActions>,
            ]}
          />
        }
        actions={
          <Stack direction="row" spacing={1} alignItems="center">
            {referenced > 0 && (
              <Chip
                size="small"
                variant="outlined"
                label={`used in ${referenced} place${referenced === 1 ? '' : 's'}`}
                onClick={() => setTab('used_by')}
              />
            )}
            {canManage && (
              <Tooltip
                title={
                  referenced > 0
                    ? 'Deletion is blocked while the ruleset is referenced (see Used in).'
                    : 'Delete this ruleset'
                }
              >
                <span>
                  <IconButton
                    size="small"
                    aria-label="Delete ruleset"
                    color="error"
                    onClick={() => setConfirmDelete(true)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Stack>
        }
      />

      <ConflictBanner
        open={conflict}
        onReload={handleConflictReload}
        message="This ruleset changed on the server (another editor). Reload before saving — your unsaved edits will be discarded."
      />

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v as 'general' | 'used_by' | 'history')}
        sx={{ mb: 2 }}
      >
        <Tab value="general" label="General" />
        <Tab value="used_by" label="Used in" />
        {hasPerm('console_audit_view') && <Tab value="history" label="History" />}
      </Tabs>

      {tab === 'general' ? (
        <Stack spacing={2}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
              <TextField
                size="small"
                label="Name"
                required
                sx={{ flex: 2, minWidth: 260 }}
                value={draft.draft.name}
                disabled={!canManage}
                error={Boolean(fieldErrors.name) || !draft.draft.name.trim()}
                helperText={fieldErrors.name}
                onChange={(e) => draft.setMeta('name', e.target.value)}
              />
              <TextField
                size="small"
                label="Codename"
                sx={{ flex: 1, minWidth: 220 }}
                value={draft.draft.codename}
                disabled={!canManage}
                error={codenameInvalid || Boolean(fieldErrors.codename)}
                helperText={
                  fieldErrors.codename ??
                  (codenameInvalid
                    ? 'Must start with a letter; letters, digits, hyphens, underscores only.'
                    : 'Optional alias for {% if-ruleset %} tags; unique per operator.')
                }
                onChange={(e) => draft.setMeta('codename', e.target.value)}
                InputProps={{
                  endAdornment: (
                    <>
                      {/* `{% if-ruleset %}` tags name this codename — the
                          find-replace corpus is where you see who does. */}
                      <FieldActionsAdornment
                        actions={
                          draft.draft.codename.trim()
                            ? codenameSearchActions(draft.draft.codename.trim())
                            : []
                        }
                      />
                      <FieldHelp help={RULESET_HELP.codename} />
                    </>
                  ),
                }}
              />
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <RulesEditor
              value={draft.draft.rules}
              onChange={(rules) => {
                draft.setRules(rules);
                setRuleErrors({}); // server row errors are keyed by index — stale after an edit
              }}
              matchType={draft.draft.match_type}
              onMatchTypeChange={(v) => draft.setMeta('match_type', v)}
              tenantId={detail.tenant?.id ?? 'system'}
              excludeRulesetId={detail.id}
              errors={{ ...ruleIssues.errors, ...ruleErrors }}
              warnings={ruleIssues.warnings}
              disabled={!canManage}
              emptyMatchesLabel="everything (the ruleset always passes)"
            />
          </Paper>

          <Typography variant="caption" color="text.secondary">
            Rules written here are fully compatible with the operator dashboard&apos;s ruleset
            editor.
          </Typography>
        </Stack>
      ) : tab === 'used_by' ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <UsedByPanel resource="notification_rulesets" id={detail.id} />
        </Paper>
      ) : (
        <HistoryPanel
          contentType="demo.notification_ruleset"
          objectPk={String(detail.id)}
          onRestored={handleConflictReload}
        />
      )}

      <EditorSaveBar
        dirty={draft.dirty}
        saving={saveMutation.isPending}
        canManage={canManage}
        onSave={() => {
          if (!draft.draft.name.trim()) {
            snackbar.show('Name is required.', 'error');
            return;
          }
          if (codenameInvalid) {
            snackbar.show('Fix the codename before saving.', 'error');
            return;
          }
          if (ruleIssues.blocking) {
            snackbar.show('Fix the rules marked in red before saving.', 'error');
            return;
          }
          saveMutation.mutate();
        }}
        onDiscard={() => {
          draft.reset(detail);
          clearErrors();
        }}
        guard={!deleted}
        guardMessage="You have unsaved changes on this ruleset. Leaving now will discard them."
      />

      <ConfirmDialog
        open={confirmDelete}
        title="Delete this ruleset?"
        message={
          referenced > 0
            ? `"${title}" is still referenced in ${referenced} place(s) — the server will refuse the deletion. Remove those references first (see the Used-in tab).`
            : `"${title}" and its rules will be deleted. This cannot be undone.`
        }
        confirmLabel="Delete ruleset"
        destructive
        onConfirm={() => {
          setConfirmDelete(false);
          deleteMutation.mutate();
        }}
        onCancel={() => setConfirmDelete(false)}
      />
    </Box>
  );
}
